import { useState } from "react";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import type {
  Assignment,
  ExerciseMode,
  Practitioner,
  SlotTemplate,
} from "@/api/types";

type Block = {
  startSlot: SlotTemplate;
  endSlot: SlotTemplate;
  practitionerId: string;
  practitionerLabel: string;
  modeCode: string | null;
  assignments: Assignment[];
};

function timeToMinutes(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

function paletteIndex(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h;
}

const PALETTE_LIGHT = [
  "#1976d2", "#7b1fa2", "#2e7d32", "#ef6c00", "#c2185b",
  "#0288d1", "#5d4037", "#00838f", "#827717", "#6a1b9a",
];
const PALETTE_DARK = [
  "#64b5f6", "#ce93d8", "#81c784", "#ffb74d", "#f48fb1",
  "#4fc3f7", "#bcaaa4", "#4dd0e1", "#dce775", "#ba68c8",
];

function colorForPractitioner(id: string, mode: "light" | "dark"): string {
  const palette = mode === "dark" ? PALETTE_DARK : PALETTE_LIGHT;
  return palette[paletteIndex(id) % palette.length]!;
}

/**
 * Build horizontal blocks for one day: consecutive slot templates assigned to
 * the SAME practitioner with end_time == next.start_time are collapsed into a
 * single block.
 */
function buildDayBlocks(
  daySlots: SlotTemplate[],
  assignmentsByKey: Map<string, Assignment[]>,
  date: string,
  practitionerById: Map<string, Practitioner>,
  modeById: Map<string, ExerciseMode>,
): Block[] {
  // Sort by start_time for safe consecutive detection
  const slots = [...daySlots].sort(
    (a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time),
  );
  const out: Block[] = [];

  // Each cell can carry multiple assignments; we emit a block PER practitioner.
  // We walk slot-by-slot, maintaining open blocks per practitioner.
  const open = new Map<string, Block>();

  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i]!;
    const list = assignmentsByKey.get(`${date}__${slot.id}`) ?? [];
    const presentNow = new Set(list.map((a) => a.practitioner_id));

    // Close any open block whose practitioner is not on this slot, or
    // whose previous slot doesn't connect time-wise to this one.
    for (const [pid, blk] of Array.from(open.entries())) {
      const connectedInTime = blk.endSlot.end_time === slot.start_time;
      if (!presentNow.has(pid) || !connectedInTime) {
        out.push(blk);
        open.delete(pid);
      }
    }

    // Extend or open blocks for each practitioner present on this slot
    for (const a of list) {
      const pid = a.practitioner_id;
      const prac = practitionerById.get(pid);
      const mode = modeById.get(a.exercise_mode_id);
      const existing = open.get(pid);
      if (existing) {
        existing.endSlot = slot;
        existing.assignments.push(a);
        if (!existing.modeCode && mode) existing.modeCode = mode.code;
      } else {
        open.set(pid, {
          startSlot: slot,
          endSlot: slot,
          practitionerId: pid,
          practitionerLabel: prac?.display_name ?? pid.slice(0, 8),
          modeCode: mode?.code ?? null,
          assignments: [a],
        });
      }
    }
  }

  // Flush remaining open blocks
  for (const blk of open.values()) out.push(blk);
  return out;
}


export type BlockAction =
  | { kind: "move"; assignments: Assignment[]; newDate: string }
  | { kind: "duplicate"; assignments: Assignment[]; newDate: string }
  | { kind: "delete"; assignments: Assignment[] }
  | { kind: "copy"; assignments: Assignment[] }
  | { kind: "paste"; date: string; slotId: string };

export function PlanningCompactView({
  year,
  month,
  days,
  slots,
  assignmentsBySlot,
  practitionerById,
  modeById,
  canEdit = false,
  onBlockAction,
}: {
  year: number;
  month: number;
  days: number[];
  slots: SlotTemplate[];
  assignmentsBySlot: Map<string, Assignment[]>;
  practitionerById: Map<string, Practitioner>;
  modeById: Map<string, ExerciseMode>;
  canEdit?: boolean;
  onBlockAction?: (action: BlockAction) => void;
}) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const mode = theme.palette.mode;

  // Context menu state — right-click on a block opens a small Menu, then
  // optionally a dialog (move / duplicate / delete).
  const [menu, setMenu] = useState<{
    x: number;
    y: number;
    block: Block;
  } | null>(null);
  const [dialog, setDialog] = useState<{
    kind: "move" | "duplicate";
    block: Block;
    newDate: string;
  } | null>(null);

  const submitDateAction = () => {
    if (!dialog || !onBlockAction) return;
    onBlockAction({
      kind: dialog.kind,
      assignments: dialog.block.assignments,
      newDate: dialog.newDate,
    });
    setDialog(null);
  };

  const deleteBlock = (block: Block) => {
    if (!onBlockAction) return;
    onBlockAction({ kind: "delete", assignments: block.assignments });
  };

  // Day window = sum of all slot durations (assume same template for every day)
  const dayStartMin =
    slots.length > 0
      ? Math.min(...slots.map((s) => timeToMinutes(s.start_time)))
      : 0;
  const dayEndMin =
    slots.length > 0
      ? Math.max(...slots.map((s) => timeToMinutes(s.end_time)))
      : 24 * 60;
  const dayDuration = Math.max(1, dayEndMin - dayStartMin);

  const weekday = (y: number, m: number, d: number): number => {
    const wd = new Date(y, m - 1, d).getDay();
    return wd === 0 ? 7 : wd;
  };

  return (
    <Stack spacing={0.75} sx={{ overflowX: "auto" }}>
      {/* Time axis */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `90px 1fr`,
          alignItems: "center",
          minWidth: 720,
        }}
      >
        <Box />
        <Box sx={{ position: "relative", height: 18 }}>
          {Array.from({ length: Math.floor(dayDuration / 60) + 1 }).map((_, i) => {
            const totalMin = dayStartMin + i * 60;
            const h = Math.floor(totalMin / 60);
            const pct = ((totalMin - dayStartMin) / dayDuration) * 100;
            return (
              <Typography
                key={i}
                variant="caption"
                sx={{
                  position: "absolute",
                  left: `${pct}%`,
                  transform: "translateX(-50%)",
                  fontFamily: "monospace",
                  color: "text.secondary",
                  fontSize: 10,
                }}
              >
                {h}h
              </Typography>
            );
          })}
        </Box>
      </Box>

      {days.map((d) => {
        const date = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
        const dow = weekday(year, month, d);
        const isWeekend = dow >= 6;
        const blocks = buildDayBlocks(
          slots,
          assignmentsBySlot,
          date,
          practitionerById,
          modeById,
        );
        // Group blocks by practitioner so each gets its own horizontal track.
        const byPrac = new Map<string, Block[]>();
        for (const blk of blocks) {
          const arr = byPrac.get(blk.practitionerId) ?? [];
          arr.push(blk);
          byPrac.set(blk.practitionerId, arr);
        }
        // Stable order: by earliest block start_time, then by name
        const tracks = Array.from(byPrac.entries())
          .map(([pid, blks]) => {
            blks.sort(
              (a, b) =>
                timeToMinutes(a.startSlot.start_time) -
                timeToMinutes(b.startSlot.start_time),
            );
            return {
              practitionerId: pid,
              practitionerLabel: blks[0]!.practitionerLabel,
              blocks: blks,
              earliest: timeToMinutes(blks[0]!.startSlot.start_time),
            };
          })
          .sort(
            (a, b) =>
              a.earliest - b.earliest ||
              a.practitionerLabel.localeCompare(b.practitionerLabel),
          );

        const trackCount = Math.max(1, tracks.length);

        return (
          <Box
            key={d}
            sx={{
              display: "grid",
              gridTemplateColumns: `90px 1fr`,
              alignItems: "stretch",
              minWidth: 720,
              bgcolor: isWeekend ? "action.hover" : "transparent",
              borderRadius: 1,
            }}
          >
            <Box sx={{ p: 0.75, display: "flex", flexDirection: "column" }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {String(d).padStart(2, "0")}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                {t(`planning.weekday.${dow}`)}
              </Typography>
              {tracks.length > 1 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: 10, lineHeight: 1.2 }}
                >
                  {t("planning.tracksCount", { count: tracks.length })}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                position: "relative",
                m: 0.5,
                borderRadius: 0.75,
                border: 1,
                borderColor: "divider",
                bgcolor: isWeekend
                  ? alpha(theme.palette.action.hover, 0.5)
                  : "background.default",
                minHeight: 28 * trackCount + 4 * (trackCount - 1),
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                p: 0.5,
              }}
            >
              {tracks.length === 0 && (
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ textAlign: "center", py: 0.5 }}
                >
                  {t("planning.dayEmpty")}
                </Typography>
              )}
              {tracks.map((track) => (
                <Box
                  key={track.practitionerId}
                  sx={{
                    position: "relative",
                    height: 24,
                    bgcolor: "action.hover",
                    borderRadius: 0.5,
                  }}
                >
                  {track.blocks.map((blk, idx) => {
                    const startMin = timeToMinutes(blk.startSlot.start_time);
                    const endMin = timeToMinutes(blk.endSlot.end_time);
                    const leftPct = ((startMin - dayStartMin) / dayDuration) * 100;
                    const widthPct = ((endMin - startMin) / dayDuration) * 100;
                    const color = colorForPractitioner(blk.practitionerId, mode);
                    return (
                      <Tooltip
                        key={idx}
                        title={
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
                              {blk.practitionerLabel}
                            </Typography>
                            <Typography variant="caption">
                              {blk.startSlot.start_time.slice(0, 5)}–{blk.endSlot.end_time.slice(0, 5)}
                              {blk.modeCode ? ` · ${blk.modeCode}` : ""}
                            </Typography>
                          </Box>
                        }
                      >
                        <Box
                          onContextMenu={(e) => {
                            if (!canEdit) return;
                            e.preventDefault();
                            setMenu({
                              x: e.clientX + 2,
                              y: e.clientY - 6,
                              block: blk,
                            });
                          }}
                          sx={{
                            position: "absolute",
                            left: `${leftPct}%`,
                            width: `${widthPct}%`,
                            top: 0,
                            bottom: 0,
                            bgcolor: mode === "dark" ? alpha(color, 0.25) : alpha(color, 0.18),
                            border: "1px solid",
                            borderColor: color,
                            borderRadius: 0.5,
                            px: 0.5,
                            display: "flex",
                            alignItems: "center",
                            overflow: "hidden",
                            cursor: canEdit ? "context-menu" : "default",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: mode === "dark" ? alpha(color, 0.95) : color,
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                            }}
                          >
                            {blk.practitionerLabel}
                            <Box component="span" sx={{ opacity: 0.7, ml: 0.5, fontSize: 9 }}>
                              {blk.startSlot.start_time.slice(0, 5)}–{blk.endSlot.end_time.slice(0, 5)}
                            </Box>
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              ))}
            </Box>
          </Box>
        );
      })}

      {/* Footer legend */}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
        {t("planning.compactHint")} · {new Date(year, month - 1).toLocaleDateString(i18n.language, { month: "long", year: "numeric" })}
      </Typography>

      <Menu
        open={menu !== null}
        onClose={() => setMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={menu ? { top: menu.y, left: menu.x } : undefined}
      >
        <MenuItem disabled>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {menu?.block.practitionerLabel} ·{" "}
            {menu?.block.startSlot.start_time.slice(0, 5)}–
            {menu?.block.endSlot.end_time.slice(0, 5)}
          </Typography>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (!menu) return;
            setDialog({ kind: "move", block: menu.block, newDate: "" });
            setMenu(null);
          }}
        >
          {t("planning.blockMove")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!menu) return;
            setDialog({ kind: "duplicate", block: menu.block, newDate: "" });
            setMenu(null);
          }}
        >
          {t("planning.blockDuplicate")}
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (!menu) return;
            deleteBlock(menu.block);
            setMenu(null);
          }}
          sx={{ color: "error.main" }}
        >
          {t("planning.blockDelete")}
        </MenuItem>
      </Menu>

      <Dialog
        open={dialog !== null}
        onClose={() => setDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {dialog?.kind === "move"
            ? t("planning.moveDialogTitle")
            : t("planning.duplicateDialogTitle")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {dialog?.block.practitionerLabel} ·{" "}
              {dialog?.block.startSlot.start_time.slice(0, 5)}–
              {dialog?.block.endSlot.end_time.slice(0, 5)}
            </Typography>
            <TextField
              type="date"
              label={t("planning.targetDate")}
              value={dialog?.newDate ?? ""}
              onChange={(e) =>
                setDialog((d) => (d ? { ...d, newDate: e.target.value } : d))
              }
              InputLabelProps={{ shrink: true }}
              autoFocus
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            disabled={!dialog?.newDate}
            onClick={submitDateAction}
          >
            {dialog?.kind === "move"
              ? t("planning.blockMove")
              : t("planning.blockDuplicate")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
